import {
    Alert, Box, Chip, CircularProgress, Collapse, Divider,
    IconButton, Snackbar, Typography
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import moment from 'moment';
import ApiServices from '../../services/ApiServices';

const stageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_SUCCESS' || stage === 'COMPLETED') return 'success';
    if (stage === 'OTA_FAILED' || stage === 'FAILED' || stage === 'REQUEST_REJECTED_ACTIVE_SESSION' || stage === 'DOWNLOAD_FAILED') return 'error';
    if (stage === 'STOPPED' || stage === 'STOP_REJECTED_NO_ACTIVE_SESSION') return 'warning';
    if (stage === 'PENDING') return 'default';
    return 'info';
};

const ERROR_CODE_COLUMN = {
    field: 'errorCode', headerName: 'Error Code', flex: 0.6, sortable: false,
    renderCell: (params) => params.row.errorCode != null
        ? <Typography variant="caption">{params.row.errorCode}</Typography>
        : <Typography variant="caption" color="text.secondary">—</Typography>
};

const BASE_DEVICE_COLUMNS = [
    { field: 'deviceMac', headerName: 'Device MAC', flex: 1, sortable: false },
    { field: 'binaryType', headerName: 'Binary', flex: 0.6, sortable: false },
    {
        field: 'stage', headerName: 'Stage', flex: 0.8, sortable: false,
        renderCell: (params) => (
            <Chip label={params.row.stage ?? '—'} color={stageColor(params.row.stage)} size="small" />
        )
    },
    {
        field: 'infoMsg', headerName: 'Last Status', flex: 1.5, sortable: false,
        renderCell: (params) => (
            <Typography variant="caption" sx={{ whiteSpace: 'normal', lineHeight: 1.4, wordBreak: 'break-word' }}>
                {params.row.infoMsg ?? '—'}
            </Typography>
        )
    },
    {
        field: 'updatedAt', headerName: 'Updated', flex: 1, sortable: false,
        renderCell: (params) => params.value
            ? moment(params.value).format('DD-MM-YYYY HH:mm:ss')
            : '—'
    },
];

const isXYZ = (v) => /^\d+\.\d+\.\d+/.test(v ?? '');

const isXY = (v) => /^\d+\.\d+$/.test(v ?? '');

const isV = (v) => (v ?? '').startsWith('v');

function LogFeed({ logs }) {
    if (!logs || logs.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">No messages recorded.</Typography>
        );
    }
    return (
        <Box sx={{
            maxHeight: 400,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            bgcolor: 'background.paper',
        }}>
            {logs.map((log) => (
                <Box
                    key={log.id}
                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.75 }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap', minWidth: 75, pt: '2px' }}
                    >
                        {moment(log.createdAt).format('HH:mm:ss')}
                    </Typography>
                    <Chip
                        label={log.deviceMac ? log.deviceMac.slice(-8) : 'Gateway'}
                        size="small"
                        color={log.deviceMac ? 'primary' : 'default'}
                        variant={log.deviceMac ? 'outlined' : 'filled'}
                        sx={{ minWidth: 72, fontSize: '0.65rem', height: 18 }}
                    />
                    <Typography variant="caption" sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
                        {log.infoMsg}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function GatewaySessionDetail({ otaId, coordinatorVersion }) {
    const [session, setSession] = useState(null);
    const [firmwares, setFirmwares] = useState([]);
    const [deviceStatuses, setDeviceStatuses] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snack, setSnack] = useState({ open: false, severity: 'error', message: '' });
    const [logsOpen, setLogsOpen] = useState(false);

    useEffect(() => {
        ApiServices.getOtaSession(otaId)
            .then(response => {
                const { session: s, firmwares: fw, deviceStatuses: ds, logs: lg } = response.data;
                setSession(s);
                setFirmwares(fw ?? []);
                setDeviceStatuses((ds ?? []).slice().sort((a, b) => a.id - b.id));
                setLogs(lg ?? []);
            })
            .catch(error => {
                setSnack({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setLoading(false));
    }, [otaId]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>;
    }

    if (!session) {
        return <Typography color="text.secondary">Session not found.</Typography>;
    }

    const deviceColumns = (isXYZ(coordinatorVersion) || isV(coordinatorVersion))
        ? [...BASE_DEVICE_COLUMNS.slice(0, -1), ERROR_CODE_COLUMN, BASE_DEVICE_COLUMNS.at(-1)]
        : BASE_DEVICE_COLUMNS;

    return (
        <Box sx={{ minWidth: 600 }}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={snack.open}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
            </Snackbar>

            {/* Summary + Firmwares side by side */}
            <Box sx={{ display: 'flex', gap: 3, mb: 1.5, alignItems: 'flex-start' }}>
                {/* Left: session details */}
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2"><b>OTA ID:</b> {session.otaId}</Typography>
                    <Typography variant="body2"><b>Gateway:</b> {session.gatewayId}</Typography>
                    <Typography variant="body2"><b>Stage:</b>{' '}
                        <Chip label={session.stage} color={stageColor(session.stage)} size="small" />
                    </Typography>
                    {isXY(coordinatorVersion) && session.startTime != null && (
                        <Typography variant="body2"><b>Start Time:</b> {session.startTime}s</Typography>
                    )}
                    {isXY(coordinatorVersion) && session.stopTime != null && (
                        <Typography variant="body2"><b>Stop Time:</b> {session.stopTime}s</Typography>
                    )}
                    <Typography variant="body2"><b>Started by:</b> {session.updatedBy ?? '—'}</Typography>
                    <Typography variant="body2">
                        <b>Started:</b> {session.createdAt ? moment(session.createdAt).format('DD-MM-YYYY HH:mm:ss') : '—'}
                    </Typography>
                    <Typography variant="body2">
                        <b>Updated:</b> {session.updatedAt ? moment(session.updatedAt).format('DD-MM-YYYY HH:mm:ss') : '—'}
                    </Typography>
                    {session.infoMsg && (
                        <Typography variant="body2" color="text.secondary">
                            <b>Gateway:</b> {session.infoMsg}
                        </Typography>
                    )}
                </Box>

                {/* Right: firmware list */}
                {firmwares.length > 0 && (
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Firmwares</Typography>
                        {firmwares.map(fw => (
                            <Typography key={fw.id} variant="body2">
                                {fw.binaryType} — {fw.version}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Status message log */}
            <Divider sx={{ my: 1.5 }} />
            <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: logsOpen ? 1 : 0 }}
                onClick={() => setLogsOpen(o => !o)}
            >
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    Status Log {logs.length > 0 && `(${logs.length})`}
                </Typography>
                <IconButton size="small">
                    {logsOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>
            <Collapse in={logsOpen}>
                <LogFeed logs={logs} />
            </Collapse>

            {/* Per-device status */}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Per-device status</Typography>
            <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                <DataGrid
                    rows={deviceStatuses}
                    columns={deviceColumns}
                    getRowId={(row) => row.id}
                    autoHeight
                    hideFooter
                    disableColumnMenu
                    disableSelectionOnClick
                    getRowHeight={() => 'auto'}
                    getEstimatedRowHeight={() => 52}
                    sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
                    slots={{
                        noRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography color="text.secondary">No device status recorded</Typography>
                            </Box>
                        )
                    }}
                />
            </Box>
        </Box>
    );
}

export default GatewaySessionDetail;